import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FailureDiagnostic } from '@chatsdet/types/issues';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

type DiagnosticDto = {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  diagnostic: FailureDiagnostic | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
};

export function useExternalFailureDiagnostic(
  runId: string,
  itemId: string | null,
  enabled: boolean
) {
  const queryClient = useQueryClient();
  const queryKey = ['external-failure-diagnostic', runId, itemId] as const;
  const query = useQuery<DiagnosticDto | null>({
    queryKey,
    enabled: enabled && Boolean(itemId),
    queryFn: async () => {
      const response = await fetch(
        `${API_URL}/external-tests/runs/${runId}/items/${itemId}/failure-diagnostic`,
        { credentials: 'include' }
      );
      if (!response.ok) throw await toApiError(response, 'Failed to load failure diagnostic');
      const body = (await response.json()) as { ok: true; data: DiagnosticDto | null };
      return body.data;
    },
    refetchInterval: (state) => {
      const status = state.state.data?.status;
      return status === 'pending' || status === 'running' ? 3000 : false;
    },
  });
  const trigger = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `${API_URL}/external-tests/runs/${runId}/items/${itemId}/failure-diagnostic`,
        { method: 'POST', credentials: 'include' }
      );
      if (!response.ok) throw await toApiError(response, 'Failed to start failure diagnostic');
      const body = (await response.json()) as { ok: true; data: DiagnosticDto };
      return body.data;
    },
    onSuccess: (data) => queryClient.setQueryData(queryKey, data),
  });

  return {
    id: query.data?.id ?? null,
    status: query.data?.status ?? null,
    diagnostic: query.data?.diagnostic ?? null,
    errorMessage: query.data?.errorMessage ?? null,
    isLoading: query.isLoading,
    isTriggering: trigger.isPending,
    triggerDiagnostic: () => trigger.mutate(),
  };
}
